import React, { useRef, useEffect } from 'react';
import T from 'prop-types';
import useEventListener from '../../hooks/useEventListener';
import CONSTANTS from '../../../../../shared/constants';

const PreviewAudioPlayer = ({
  src,
  adMarkers,
  isPlaying,
  setPlaying,
  setPlayingAd,
  setAudioLoading,
  setCurrentTime,
  currentTime,
  seek,
  setAudioElement,
}) => {
  const mainAudio = useRef();
  const adJingleAudio = useRef();

  const setAudioState = (event) => {
    const {
      type,
      target: { currentTime: currentEventTime },
    } = event;

    if (type === 'play') {
      setPlaying(true);
    }

    if (type === 'pause') {
      setPlaying(false);
    }

    if (type === 'waiting') {
      setAudioLoading(true);
    }

    if (type === 'canplay') {
      setAudioLoading(false);
    }

    if (type === 'timeupdate') {
      if (isPlaying) {
        maybePlayAdJingle(currentTime, currentEventTime);
      }
      setCurrentTime(currentEventTime);
    }

    if (type === 'ended') {
      if (isPlaying) {
        maybePlayAdJingle(currentTime, currentEventTime);
      }
      setCurrentTime(currentEventTime);
      setPlaying(false);
    }
  };

  const setAdAudioState = (event) => {
    const { type } = event;

    if (type === 'ended') {
      setPlayingAd(false);
      play();
    }
  };

  const play = () => {
    mainAudio.current.play();
    setPlaying(true);
  };

  const pause = () => {
    mainAudio.current.pause();
    setPlaying(false);
  };

  const playAd = () => {
    adJingleAudio.current.play();
    setPlayingAd(true);
  };

  useEventListener('timeupdate', setAdAudioState, adJingleAudio.current);
  useEventListener('canplay', setAdAudioState, adJingleAudio.current);
  useEventListener('waiting', setAdAudioState, adJingleAudio.current);
  useEventListener('ended', setAdAudioState, adJingleAudio.current);
  useEventListener('play', setAdAudioState, adJingleAudio.current);
  useEventListener('pause', setAdAudioState, adJingleAudio.current);

  useEventListener('timeupdate', setAudioState, mainAudio.current);
  useEventListener('ended', setAudioState, mainAudio.current);
  useEventListener('canplay', setAudioState, mainAudio.current);
  useEventListener('waiting', setAudioState, mainAudio.current);
  useEventListener('seeked', setAudioState, mainAudio.current);
  useEventListener('play', setAudioState, mainAudio.current);
  useEventListener('pause', setAudioState, mainAudio.current);

  useEffect(() => {
    mainAudio.current.currentTime = seek;
    setCurrentTime(seek);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seek]);

  useEffect(() => {
    setAudioElement(mainAudio.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const maybePlayAdJingle = (previousMainAudioProgress, nextMainAudioProgress) => {
    const hasAdMarkerInBetween = adMarkers.some(
      (adMarker) =>
        adMarker.start > previousMainAudioProgress && adMarker.start < nextMainAudioProgress
    );

    if (hasAdMarkerInBetween) {
      pause();
      playAd();
    }
  };

  return (
    <>
      <audio
        ref={adJingleAudio}
        src={CONSTANTS.AD_PREBUMP}
        data-testid="preview-audio-player-jingle"
      />
      <audio ref={mainAudio} src={src} data-testid="preview-audio-player-main" />
    </>
  );
};

PreviewAudioPlayer.propTypes = {
  adMarkers: T.array,
  src: T.string,
  isPlaying: T.bool,
  setPlaying: T.func,
  setPlayingAd: T.func,
  setAudioLoading: T.func,
  setCurrentTime: T.func,
  currentTime: T.number,
  seek: T.number,
  setAudioElement: T.func,
};

export default PreviewAudioPlayer;
